import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Info } from 'lucide-react';
import { COPYRIGHT_STATUS_LABELS, type CopyrightStatus, type TextbookSummary } from '@en3/shared';
import { contentApi } from '@/api/content.api';
import { isApiError, toDisplayMessage } from '@/api/errors';
import { useAuthStatus } from '@/api/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { FormSelect, FormTextarea } from './ArticleCard';

/**
 * 新建教材（教师 / 管理员）。
 *
 * 设计要点：
 *
 * 1. **`slug` 不暴露给用户。** 它是幂等写入的稳定键（小写 + 连字符），
 *    让教师去理解"slug 与标题的区别"是多余负担，留空由服务端按标题生成即可。
 *    如果用户确实需要（例如批量脚本按 slug 对齐），可以之后再补一个"高级"折叠区。
 *
 * 2. **版权状态给出显式选项，而不是默认埋掉。** 平台收录《大学英语》系列时
 *    状态必须是"仅公开目录元数据"（`public_metadata`）；如果教师自己撰写了教材，
 *    可以选"公版作品"。这个字段直接决定目录页上显示什么合规提示，值得占一行。
 *
 * 3. **`level` 默认继承目录页当前的筛选值**（通常是「大学英语三」），
 *    避免用户建完教材后在原筛选条件下"看不到自己刚建的那本"。
 */

export interface CreateTextbookDialogProps {
  open: boolean;
  onClose: () => void;
  /** 继承目录页当前的 level 筛选，保证新建后仍在该筛选里可见 */
  defaultLevel: string;
  /** 创建成功后由页面失效并刷新目录 */
  onCreated: (textbook: TextbookSummary) => void;
}

interface FormState {
  title: string;
  titleZh: string;
  publisher: string;
  edition: string;
  level: string;
  description: string;
  copyrightStatus: CopyrightStatus;
  visibility: 'public' | 'private';
}

/**
 * 教材层面可选的版权状态。
 *
 * 刻意**不**把 `user_imported` / `user_authorized` / `takedown` 列出来：
 * 前两者是"某篇文章由用户导入"的留痕语义，放在教材上会让人误以为整套教材都有授权；
 * 后者是权利人投诉后的处置结果，不该由教师手动设置。
 */
const TEXTBOOK_COPYRIGHT_OPTIONS: CopyrightStatus[] = ['public_metadata', 'public_domain', 'licensed'];

export function CreateTextbookDialog({ open, onClose, defaultLevel, onCreated }: CreateTextbookDialogProps) {
  const status = useAuthStatus();
  const [form, setForm] = useState<FormState>(() => emptyForm(defaultLevel));
  const [error, setError] = useState<string | null>(null);

  // 只在"刚打开的那一次"重置表单：目录页的 level 筛选随时可能变，
  // 如果把它放进依赖数组，用户在筛选框里敲字就会把填了一半的对话框清空。
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setForm(emptyForm(defaultLevel));
      setError(null);
    }
    wasOpen.current = open;
  }, [open, defaultLevel]);

  const createMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => contentApi.createTextbook(input),
  });

  function patch(next: Partial<FormState>) {
    setForm((current) => ({ ...current, ...next }));
  }

  const canSubmit = status === 'authenticated' && form.title.trim().length > 0 && form.level.trim().length > 0;

  async function submit() {
    setError(null);
    try {
      const data = await createMutation.mutateAsync({
        title: form.title.trim(),
        level: form.level.trim(),
        copyrightStatus: form.copyrightStatus,
        visibility: form.visibility,
        ...(form.titleZh.trim() ? { titleZh: form.titleZh.trim() } : {}),
        ...(form.publisher.trim() ? { publisher: form.publisher.trim() } : {}),
        ...(form.edition.trim() ? { edition: form.edition.trim() } : {}),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
      });
      onCreated(data.textbook);
    } catch (cause) {
      // 后端的 zod 校验失败会带 issues，逐字段的中文提示比"输入不合法"具体得多
      const fieldIssue = isApiError(cause) ? cause.fieldIssues[0] : undefined;
      setError(fieldIssue ? fieldIssue.message : toDisplayMessage(cause));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      variant="sheet"
      title="新建教材"
      className="sm:max-w-xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={createMutation.isPending}>
            创建教材
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="flex items-start gap-1.5 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Info aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          教材只承载目录结构（册次、单元、文章归类）。课文正文由「导入文章」单独导入，
          平台不代你收录受版权保护的教材全文。
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field id="tb-title" label="教材名称" hint="必填，例如：新视野大学英语读写教程 3">
            <Input
              id="tb-title"
              value={form.title}
              onChange={(event) => patch({ title: event.target.value })}
              placeholder="教材名称"
            />
          </Field>
          <Field id="tb-title-zh" label="中文/副标题" hint="可选，教材原名已是中文时可留空">
            <Input
              id="tb-title-zh"
              value={form.titleZh}
              onChange={(event) => patch({ titleZh: event.target.value })}
            />
          </Field>
          <Field id="tb-publisher" label="出版社" hint="可选">
            <Input
              id="tb-publisher"
              value={form.publisher}
              onChange={(event) => patch({ publisher: event.target.value })}
            />
          </Field>
          <Field id="tb-edition" label="版次" hint="可选，例如：第三版">
            <Input id="tb-edition" value={form.edition} onChange={(event) => patch({ edition: event.target.value })} />
          </Field>
          <Field id="tb-level" label="适用等级" hint="目录页按这个值筛选，默认沿用当前筛选">
            <Input id="tb-level" value={form.level} onChange={(event) => patch({ level: event.target.value })} />
          </Field>
          <Field id="tb-visibility" label="可见性" hint="私有教材只有你自己能在目录页看到">
            <FormSelect
              id="tb-visibility"
              value={form.visibility}
              onChange={(event) => patch({ visibility: event.target.value === 'private' ? 'private' : 'public' })}
              className="w-full"
            >
              <option value="public">公开</option>
              <option value="private">私有</option>
            </FormSelect>
          </Field>
          <Field id="tb-copyright" label="版权状态" hint="决定目录页显示的合规提示">
            <FormSelect
              id="tb-copyright"
              value={form.copyrightStatus}
              onChange={(event) =>
                patch({ copyrightStatus: event.target.value as FormState['copyrightStatus'] })
              }
              className="w-full"
            >
              {TEXTBOOK_COPYRIGHT_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {COPYRIGHT_STATUS_LABELS[value]}
                </option>
              ))}
            </FormSelect>
          </Field>
        </div>

        <Field id="tb-description" label="简介" hint="可选，写清这本教材面向谁、包含哪些单元主题">
          <FormTextarea
            id="tb-description"
            rows={3}
            value={form.description}
            onChange={(event) => patch({ description: event.target.value })}
          />
        </Field>

        {error && (
          <p
            role="alert"
            className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            <AlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function emptyForm(defaultLevel: string): FormState {
  return {
    title: '',
    titleZh: '',
    publisher: '',
    edition: '',
    level: defaultLevel || '大学英语三',
    description: '',
    copyrightStatus: 'public_metadata',
    visibility: 'public',
  };
}
